import React, { useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import { useSelector, useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import styled from "styled-components";

import BucketSlot from "features/tiers/components/BucketSlot";
import ButtonContainer from "components/forms/shared/ButtonContainer";
import BucketForm from "features/buckets/forms/Bucket";
import { updateBucket } from "features/buckets/thunks";
import ModalHeader from "../../../../components/modals/Header";
import ModalSubheader from "../../../../components/modals/Subheader";
import BaseModal from "../../../../components/modals/Base";
import Button from "components/buttons/Button";

const StyledModalHeader = styled.div`
  font-family: "SuisseIntl";
  font-style: normal;
  font-weight: 900;
  font-size: 26px;
  line-height: 26px;
  letter-spacing: 0.06em;
  color: ${(props) => props.theme.yellow};
  transform: matrix(1, 0.03, -0.06, 1, 0, 0);
  text-align: start !important;
  padding: 1.5rem 1.5rem 0px 1.5rem;
  margin-top: 1rem;
`;

function TierBucketsModal({ registrationTier, ...props }) {
  // Allows the user to select which pages are in a tier.
  const project = useSelector((state) => state.projects.detail);
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [activeSection, setActiveSection] = useState("chooseBuckets");

  // The bucket id that is updating.
  const [bucketIdUpdating, setBucketIdUpdating] = useState(null);

  async function handleBucketClick(bucket, isSelected) {
    /* Toggle if the bucket is selected for the given registration tier.

       :param bucket object: The bucket that is being toggled.
       :param isSelected bool: If the bucket is currently selected.
    */
    setBucketIdUpdating(bucket.id);

    const updatedTiers = isSelected
      ? bucket.registrationTiers.filter(
          (tierId) => tierId !== registrationTier.id
        )
      : bucket.registrationTiers.concat(registrationTier.id);

    let actionPayload = {
      bucketId: bucket.id,
      payload: { registrationTiers: updatedTiers },
    };

    // If we're selecting a bucket in a tier, then it should be marked as not public.
    if (!isSelected) actionPayload.payload.isPublic = false;

    const action = await dispatch(updateBucket(actionPayload));
    if (action.type === "UPDATE_BUCKET/rejected")
      addToast("Error updating bucket", { appearance: "error" });

    setBucketIdUpdating(null);
  }

  function renderBuckets() {
    // Renders all the buckets for the project so that hte user can select which to show.
    return project.buckets.map((bucket) => {
      const selectedBucketIds = registrationTier.buckets.map(
        (bucket) => bucket.id
      );
      const isSelected = selectedBucketIds.includes(bucket.id);
      const isLoading = bucketIdUpdating === bucket.id;

      return (
        <BucketSlot
          key={bucket.id}
          bucket={bucket}
          selected={isSelected}
          isLoading={isLoading}
          onClick={() => handleBucketClick(bucket, isSelected)}
        />
      );
    });
  }

  function renderContent() {
    // Render the main body based on our active section.
    if (activeSection === "chooseBuckets") {
      return (
        <>
          <StyledModalHeader>Choose Pages</StyledModalHeader>
          <ModalSubheader>
            for the <b>{registrationTier.title}</b> students
          </ModalSubheader>
          <Modal.Body>
            {renderBuckets()}
            <ButtonContainer className="mt-3 justify-content-between">
              <Button
                variant="outline-primary"
                onClick={() => setActiveSection("createBucket")}
              >
                ADD NEW PAGE
              </Button>
              <Button onClick={props.onHide}>SAVE</Button>
            </ButtonContainer>
          </Modal.Body>
        </>
      );
    } else if (activeSection === "createBucket") {
      return (
        <>
          <ModalHeader title="Create page" />
          <ModalSubheader>
            The new bucket will be viewable to <b>{registrationTier.title}</b>{" "}
            users.
          </ModalSubheader>
          <Modal.Body>
            <BucketForm
              bucket={{
                registrationTiers: [registrationTier.id],
                isPublic: false,
              }}
              backAction={() => setActiveSection("chooseBuckets")}
              closeModal={props.onHide}
            />
          </Modal.Body>
        </>
      );
    }
  }

  return <BaseModal {...props}>{renderContent()}</BaseModal>;
}

TierBucketsModal.propTypes = {
  // The registration tier for which we're choosing buckets.
  registrationTier: PropTypes.object.isRequired,
};

export default TierBucketsModal;
